<template>
    <div class="step-wrap">
        <div class="step-header shrink-0">
            <h1 class="step-h1">Where does it happen?</h1>
            <p class="step-lead">Helps match your pitch to the right creator. <span class="muted">(Pick as many as you'd like)</span></p>
        </div>

        <!-- Selected pills -->
        <div v-if="hasSelection" class="selected-pills shrink-0">
            <div v-for="id in settings" :key="id" class="pill">
                <span class="pill-text">{{ getLabel(id) }}</span>
                <button class="pill-remove" @click="$emit('removeSetting', id)"><X style="width:12px;height:12px" /></button>
            </div>
        </div>

        <!-- Grid -->
        <div class="setting-scroll hide-scrollbar">
            <div class="setting-grid">
                <button
                    v-for="s in SETTINGS"
                    :key="s.id"
                    class="setting-card"
                    :class="{ 'setting-selected': settings.includes(s.id) }"
                    :style="{ transform: settings.includes(s.id) ? 'scale(1.02) translateY(-2px)' : 'scale(1)' }"
                    @click="$emit('toggleSetting', s.id)"
                >
                    <img :src="s.image" :alt="s.name" class="setting-img" />
                    <div class="setting-overlay" :class="settings.includes(s.id) ? 'overlay-selected' : 'overlay-default'" />
                    <div v-if="settings.includes(s.id)" class="setting-check"><Check style="width:12px;height:12px" /></div>
                    <div class="setting-label">{{ s.name }}</div>
                </button>
            </div>
        </div>

        <!-- Custom setting — outside the scroll container so iOS focus doesn't scroll the grid -->
        <div class="custom-wrap shrink-0">
            <button v-if="!showCustomInput" class="custom-trigger" @click="showCustomInput = true">
                <Plus style="width:14px;height:14px" /> Custom setting
            </button>
            <div v-else class="custom-input-row">
                <input
                    ref="customInputEl"
                    v-model="customInput"
                    type="text"
                    placeholder='e.g. "Rooftop bar at sunset"'
                    class="custom-input"
                    @focus="$emit('update:inputFocused', true)"
                    @blur="$emit('update:inputFocused', false)"
                    @keydown.enter="handleAddCustom"
                />
                <button class="custom-add" :disabled="!customInput.trim()" @click="handleAddCustom">Add</button>
            </div>
        </div>

        <div class="step-footer shrink-0">
            <button class="cta-btn" @click="$emit('next')">Name my pitch</button>
            <button v-if="!hasSelection" class="skip-btn" @click="$emit('next')">Skip this step</button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { X, Plus, Check } from 'lucide-vue-next'

const props = defineProps({ settings: Array })
const emit = defineEmits(['toggleSetting', 'addCustomSetting', 'removeSetting', 'next', 'update:inputFocused'])

const showCustomInput = ref(false)
const customInput     = ref('')
const customInputEl   = ref(null)
const hasSelection    = computed(() => props.settings.length > 0)

watch(showCustomInput, v => { if (v) nextTick(() => customInputEl.value?.focus()) })

const SETTINGS = [
    { id:'bedroom',   name:'Bedroom',        image:'https://sm.naughtycdn.com/assets/wizard/setting/bedroom.png' },
    { id:'office',    name:'Office',         image:'https://sm.naughtycdn.com/assets/wizard/setting/office.png' },
    { id:'pool',      name:'Pool / Outdoors',image:'https://sm.naughtycdn.com/assets/wizard/setting/pool.png' },
    { id:'hotel',     name:'Hotel Room',     image:'https://sm.naughtycdn.com/assets/wizard/setting/hotel.png' },
    { id:'gym',       name:'Gym',            image:'https://sm.naughtycdn.com/assets/wizard/setting/gym.png' },
    { id:'kitchen',   name:'Kitchen',        image:'https://sm.naughtycdn.com/assets/wizard/setting/kitchen.png' },
    { id:'living',    name:'Living Room',    image:'https://sm.naughtycdn.com/assets/wizard/setting/living_room.png' },
    { id:'classroom', name:'Classroom',      image:'https://sm.naughtycdn.com/assets/wizard/setting/classroom.png' },
    { id:'massage',   name:'Massage Studio', image:'https://sm.naughtycdn.com/assets/wizard/setting/massage_studio.png' },
]

function getLabel(id) { return SETTINGS.find(s => s.id === id)?.name ?? id }

function handleAddCustom() {
    if (customInput.value.trim()) {
        emit('addCustomSetting', customInput.value.trim())
        customInput.value = ''
        showCustomInput.value = false
    }
}
</script>

<style scoped>
.step-wrap { display:flex; flex-direction:column; height:100%; padding:12px 16px 0; }
.step-header { margin-bottom:16px; }
.step-h1 { font-size:32px; font-weight:800; color:white; margin-bottom:6px; }
.step-lead { color:rgba(255,255,255,0.7); font-size:15px; }
.muted { color:rgba(255,255,255,0.4); }
.shrink-0 { flex-shrink:0; }
.selected-pills { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
.pill { display:flex; align-items:center; gap:6px; background:rgba(0,82,204,0.2); border:1px solid rgba(0,82,204,0.3); border-radius:999px; padding:5px 8px 5px 12px; }
.pill-text { font-size:13px; color:#4D94FF; }
.pill-remove { background:none; border:none; color:#4D94FF; cursor:pointer; padding:4px; display:flex; align-items:center; border-radius:50%; }
.setting-scroll { flex:1; min-height:0; overflow-y:auto; margin:0 -16px; padding:0 16px; -webkit-overflow-scrolling:touch; }
.setting-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; padding:6px 0 12px; }
.setting-card { position:relative; aspect-ratio:4/3; border-radius:14px; overflow:hidden; border:2px solid transparent; cursor:pointer; transition:all 0.2s; padding:0; will-change:transform; }
.setting-selected { border-color:#4D94FF; }
.setting-img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
.setting-overlay { position:absolute; inset:0; transition:all 0.3s; }
.overlay-default { background:linear-gradient(to top, rgba(0,0,0,0.6), transparent, transparent); }
.overlay-selected { background:linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.2), transparent); }
.setting-check { position:absolute; top:8px; right:8px; width:24px; height:24px; border-radius:50%; background:white; display:flex; align-items:center; justify-content:center; color:#0052CC; font-size:12px; font-weight:700; }
.setting-label { position:absolute; bottom:0; left:0; padding:10px 12px; font-weight:700; color:white; font-size:13px; text-shadow:0 1px 4px rgba(0,0,0,0.8); }
.custom-wrap { padding-top:12px; padding-bottom:20px; }
.custom-trigger { display:flex; align-items:center; gap:8px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.2); border-radius:999px; padding:8px 16px; font-size:13px; color:rgba(255,255,255,0.8); cursor:pointer; min-height:44px; }
.custom-input-row { display:flex; gap:8px; }
.custom-input { flex:1; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); border-radius:12px; padding:10px 14px; color:white; outline:none; font-size:16px !important; }
.custom-input:focus { border-color:rgba(77,148,255,0.5); }
.custom-add { background:rgba(255,255,255,0.1); border:none; color:white; padding:0 16px; border-radius:12px; font-weight:500; cursor:pointer; min-height:44px; font-size:13px; }
.custom-add:disabled { opacity:0.5; }
.step-footer { padding:12px 0 4px; display:flex; flex-direction:column; }
.cta-btn { width:100%; background:#0052CC; color:white; font-weight:700; font-size:17px; padding:14px 0; border-radius:16px; border:none; cursor:pointer; }
.skip-btn { width:100%; margin-top:10px; background:none; border:none; color:rgba(255,255,255,0.4); font-weight:500; font-size:13px; cursor:pointer; min-height:36px; }
</style>
